import React from "react";
import { Form, TimePicker, DatePicker, InputNumber, Checkbox, Select } from "antd";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { RecurrenceMode } from "../../flatbuffers/generated/archive/recurrence-mode";

const { Option } = Select;

// Stable list of IANA timezone names for the Select options.
const TIMEZONE_NAMES = moment.tz.names();

// Days of week labels (Sun=index 0 … Sat=index 6) for the weekly checkbox row.
const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

// Calendar days 1–31 for the monthly day grid.
const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

// Valid month_week_ordinal values (1st..5th) for MONTHLY_WEEKDAY's Occurrence
// select. 5 is kept selectable even though a 5th occurrence is absent most
// months — LiftCloud's validateMonthlyWeekdayTiming accepts it, and a row
// configured for "5th Friday" simply skips months without one, which is
// legitimate (e.g. quarterly-ish maintenance windows).
const MONTH_WEEK_ORDINALS = [1, 2, 3, 4, 5] as const;
const ORDINAL_LABELS = ["1st", "2nd", "3rd", "4th", "5th"] as const;

// ── Sub-components ──────────────────────────────────────────────────────────

function TimeRangeFields(): React.ReactElement {
  const [t] = useTranslation("global");
  return (
    <>
      <Form.Item
        name="start_time"
        label={t("scheduledActions.startTime", "Start time")}
        rules={[
          {
            required: true,
            message: t("scheduledActions.startTimeRequired", "Start time is required"),
          },
        ]}
      >
        <TimePicker format="HH:mm" minuteStep={5} style={{ width: "100%" }} />
      </Form.Item>
      <Form.Item
        name="stop_time"
        label={t("scheduledActions.stopTime", "Stop time")}
        tooltip={t(
          "scheduledActions.stopTimeWraparoundTooltip",
          "If Stop is earlier than (or equal to) Start, the stop fires the following calendar day — e.g. Friday 19:00 to 05:00 stops Saturday morning, not Friday morning. The day/date you configure below still refers to when the window starts.",
        )}
        rules={[
          {
            required: true,
            message: t("scheduledActions.stopTimeRequired", "Stop time is required"),
          },
        ]}
      >
        <TimePicker format="HH:mm" minuteStep={5} style={{ width: "100%" }} />
      </Form.Item>
    </>
  );
}

function TimezoneField(): React.ReactElement {
  const [t] = useTranslation("global");
  return (
    <Form.Item
      name="timezone"
      label={t("scheduledActions.timezone", "Timezone")}
      rules={[
        { required: true, message: t("scheduledActions.timezoneRequired", "Timezone is required") },
      ]}
    >
      <Select showSearch optionFilterProp="children" style={{ width: "100%" }}>
        {TIMEZONE_NAMES.map((tz) => (
          <Option key={tz} value={tz}>
            {tz}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
}

function DaysOfWeekField(): React.ReactElement {
  const [t] = useTranslation("global");
  return (
    <Form.Item
      name="days_of_week_indices"
      label={t("scheduledActions.daysOfWeek", "Days of week")}
      rules={[
        {
          validator: async (_: unknown, value: number[] | undefined) => {
            if (!value || value.length === 0) {
              return Promise.reject(
                new Error(t("scheduledActions.atLeastOneDay", "Select at least one day")),
              );
            }
            return Promise.resolve();
          },
        },
      ]}
    >
      <Checkbox.Group style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {DOW_LABELS.map((label, idx) => (
          <Checkbox key={label} value={idx}>
            {t(`scheduledActions.${label.toLowerCase()}`, label)}
          </Checkbox>
        ))}
      </Checkbox.Group>
    </Form.Item>
  );
}

// ── Props ───────────────────────────────────────────────────────────────────

export interface ScheduledActionModeFieldsProps {
  mode: RecurrenceMode;
}

// ── Main component ──────────────────────────────────────────────────────────

function ScheduledActionModeFields({
  mode,
}: ScheduledActionModeFieldsProps): React.ReactElement | null {
  const [t] = useTranslation("global");

  switch (mode) {
    case RecurrenceMode.ONE_SHOT:
      return (
        <>
          <Form.Item
            name="start_at"
            label={t("scheduledActions.startAt", "Start at")}
            rules={[
              {
                required: true,
                message: t("scheduledActions.startAtRequired", "Start date/time is required"),
              },
            ]}
          >
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="stop_at"
            label={t("scheduledActions.stopAt", "Stop at")}
            tooltip={t(
              "scheduledActions.stopAtOneShotTooltip",
              "No wraparound to worry about here — Start and Stop are both absolute dates and times, so Stop is simply whichever moment you pick.",
            )}
            rules={[
              {
                required: true,
                message: t("scheduledActions.stopAtRequired", "Stop date/time is required"),
              },
              ({ getFieldValue }) => ({
                validator: async (_: unknown, value: moment.Moment | null) => {
                  const start = getFieldValue("start_at") as moment.Moment | null;
                  if (!value || !start) return Promise.resolve();
                  if (!value.isAfter(start)) {
                    return Promise.reject(
                      new Error(t("scheduledActions.stopAfterStart", "Stop must be after start")),
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>
        </>
      );

    case RecurrenceMode.DAILY:
      return (
        <>
          <TimeRangeFields />
          <DaysOfWeekField />
          <TimezoneField />
        </>
      );

    case RecurrenceMode.WEEKLY:
      return (
        <>
          <Form.Item
            name="week_interval"
            label={t("scheduledActions.everyNWeeks", "Every N weeks")}
            tooltip={t(
              "scheduledActions.everyNWeeksTooltip",
              "Which week counts as \"on\" is anchored to the START day, even for an overnight window whose stop lands the next calendar day — a Friday-night schedule stays governed by Friday's week, not Saturday's.",
            )}
            rules={[
              { required: true },
              {
                type: "number",
                min: 1,
                message: t("scheduledActions.weekIntervalMin", "Must be at least 1"),
              },
            ]}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
          <TimeRangeFields />
          <DaysOfWeekField />
          <TimezoneField />
        </>
      );

    case RecurrenceMode.MONTHLY:
      return (
        <>
          <TimeRangeFields />
          <Form.Item
            name="days_of_month_days"
            label={t("scheduledActions.daysOfMonth", "Days of month")}
            dependencies={["month_last_day"]}
            rules={[
              ({ getFieldValue }) => ({
                validator: async (_: unknown, value: number[] | undefined) => {
                  // A monthly schedule must fire on at least one day. Accept either
                  // explicit calendar days or the "last day of month" checkbox —
                  // without this guard a 0-day MONTHLY action saves but never fires.
                  if ((value && value.length > 0) || getFieldValue("month_last_day") === true) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(
                      t(
                        "scheduledActions.atLeastOneMonthDay",
                        "Select at least one day of the month",
                      ),
                    ),
                  );
                },
              }),
            ]}
          >
            <Checkbox.Group style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {MONTH_DAYS.map((d) => (
                <Checkbox key={d} value={d} style={{ minWidth: 44 }}>
                  {d}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>
          <Form.Item
            name="month_last_day"
            valuePropName="checked"
            label={t("scheduledActions.lastDayOfMonth", "Last day of month")}
          >
            <Checkbox />
          </Form.Item>
          <TimezoneField />
        </>
      );

    case RecurrenceMode.MONTHLY_WEEKDAY:
      return (
        <>
          <TimeRangeFields />
          <Form.Item
            name="days_of_week_indices"
            label={t("scheduledActions.monthWeekday", "Weekday")}
            // Reuses the SAME underlying field DAILY/WEEKLY's checkbox group
            // writes (days_of_week_indices, a number[]) rather than a new
            // field name — a UI control here that wrote somewhere else could
            // silently leave days_of_week at 0, and the scheduler's own
            // fail-closed guard for that exact case means the row would
            // simply never fire, with no error at save time. normalize/
            // getValueProps adapt the single-select UI (one weekday) to the
            // shared array-shaped field (rendered as checkboxes elsewhere).
            normalize={(value: number | undefined) => (value === undefined ? [] : [value])}
            getValueProps={(stored: number[] | undefined) => ({ value: stored?.[0] })}
            rules={[
              {
                required: true,
                message: t("scheduledActions.monthWeekdayRequired", "Select a weekday"),
              },
            ]}
          >
            <Select style={{ width: "100%" }}>
              {DOW_LABELS.map((label, idx) => (
                <Option key={label} value={idx}>
                  {t(`scheduledActions.${label.toLowerCase()}`, label)}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="month_week_ordinal"
            label={t("scheduledActions.monthWeekOrdinal", "Occurrence")}
            dependencies={["month_week_last"]}
            rules={[
              ({ getFieldValue }) => ({
                validator: async (_: unknown, value: number | undefined) => {
                  if (getFieldValue("month_week_last") === true) return Promise.resolve();
                  if (value === undefined) {
                    return Promise.reject(
                      new Error(
                        t("scheduledActions.monthWeekOrdinalRequired", "Select an occurrence"),
                      ),
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Select
              style={{ width: "100%" }}
              options={MONTH_WEEK_ORDINALS.map((n) => ({
                value: n,
                label: t(`scheduledActions.ordinal.${n}`, ORDINAL_LABELS[n - 1]),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="month_week_last"
            valuePropName="checked"
            label={t("scheduledActions.monthWeekLast", "Last occurrence of the month")}
            tooltip={t(
              "scheduledActions.monthWeekLastTooltip",
              "Overrides Occurrence above — fires on whichever is the FINAL matching weekday in the month, whether that's the 4th or the 5th.",
            )}
          >
            <Checkbox />
          </Form.Item>
          <TimezoneField />
        </>
      );

    case RecurrenceMode.INTERVAL:
      // A recurring schedule reads as start + frequency + duration. "Stop"/
      // "window" datetimes are intentionally avoided here — picking an explicit
      // stop datetime alongside an interval implies a one-shot, which is the
      // ONE_SHOT mode's job. The stored absolute stop_at is derived from
      // start_at + duration in ScheduledActionForm.formValuesToDraft.
      return (
        <>
          <Form.Item
            name="start_at"
            label={t("scheduledActions.startAt", "Start at")}
            rules={[
              {
                required: true,
                message: t("scheduledActions.startAtRequired", "Start date/time is required"),
              },
            ]}
          >
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="interval_minutes"
            label={t("scheduledActions.everyNMinutes", "Repeat every (minutes)")}
            rules={[
              {
                required: true,
                message: t("scheduledActions.intervalRequired", "Frequency is required"),
              },
              {
                type: "number",
                min: 1,
                message: t("scheduledActions.intervalMin", "Must be at least 1"),
              },
            ]}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="duration_minutes"
            label={t("scheduledActions.durationMinutes", "Duration (minutes)")}
            tooltip={t(
              "scheduledActions.durationMinutesTooltip",
              "No wraparound here — the window is Start at plus this many minutes, an absolute stop moment, not a wrapped time-of-day.",
            )}
            rules={[
              {
                required: true,
                message: t("scheduledActions.durationRequired", "Duration is required"),
              },
              {
                type: "number",
                min: 1,
                message: t("scheduledActions.durationMin", "Must be at least 1"),
              },
            ]}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
        </>
      );

    default: {
      // TypeScript exhaustiveness guard
      const exhausted: never = mode;
      return <span>{String(exhausted)}</span>;
    }
  }
}

export default ScheduledActionModeFields;
